import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import type { RootState } from '@/store';
import { clearAuth } from '@/store/slices/authSlice';
import { useAppDispatch } from '@/hooks/useMedicalState';

// Import Tailwind medical styles
import './styles/medical-migration.css';

// Navigation and Context Providers
import { MedicalNavigationProvider } from '@/contexts/MedicalNavigationContext';
import { LocaleProvider } from '@/contexts/LocaleContext';
import { ReportExportProvider } from '@/contexts/ReportExportContext';

// Components
import { Layout } from '@/components/ui/Layout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

// Hooks
import { useAuthInitialization } from '@/hooks/useAuthInitialization';
import { useTokenExpiration } from '@/hooks/useTokenExpiration';
import { authEvents } from '@/utils/authEvents';

// Pages (lazy loaded for better performance)
const LandingPage = React.lazy(() => import('@/pages/LandingPage'));
const LoginPage = React.lazy(() => import('@/pages/Auth/LoginPage'));
const RegisterPage = React.lazy(() => import('@/pages/Auth/RegisterPage'));
const DashboardPage = React.lazy(() => import('@/pages/Dashboard/DashboardPage'));

// Patient Management Pages
const PatientsPage = React.lazy(() => import('@/pages/Patients/PatientsPage'));
const PatientDetailPage = React.lazy(() => import('@/pages/Patients/PatientDetailPage'));

// Study and DICOM Pages
const StudyViewerPage = React.lazy(() => import('@/pages/Studies/StudyViewerPage'));
const StudyMeasurementsPage = React.lazy(() => import('@/pages/Studies/StudyMeasurementsPage'));
const DicomViewerPage = React.lazy(() => import('@/pages/DICOM/DicomViewerPage'));
const DicomUploadPage = React.lazy(() => import('@/pages/DICOM/DicomUploadPage'));

// Report Pages
const ReportsPage = React.lazy(() => import('@/pages/Reports/ReportsPage'));
const ReportBuilderPage = React.lazy(() => import('@/pages/Reports/ReportBuilderPage'));
const ReportFormPage = React.lazy(() => import('@/pages/Reports/ReportFormPage'));
const DynamicReportPage = React.lazy(() => import('@/pages/Reports/DynamicReportPage'));
const ReportEditorPage = React.lazy(() => import('@/pages/Reports/ReportEditorPage'));
const ReportPreviewPage = React.lazy(() => import('@/pages/Reports/ReportPreviewPage'));
// const ConfigurationTester = React.lazy(() => import('@/pages/Reports/ConfigurationTester'));

// Admin Pages
const AdminPage = React.lazy(() => import('@/pages/Admin/AdminPage'));
const UserManagementPage = React.lazy(() => import('@/pages/Admin/UserManagementPage'));
const InstitutionManagementPage = React.lazy(() => import('@/pages/Admin/InstitutionManagementPage'));
const WorklistSetupPage = React.lazy(() => import('@/pages/Admin/WorklistSetupPage'));

// Worklist Pages
const WorklistPage = React.lazy(() => import('@/pages/WorklistPage'));

// Biopsias Tracking Pages
const BiopsiasPage = React.lazy(() => import('@/pages/Biopsias/BiopsiasPage'));

// Error and Utility Pages
const UnauthorizedPage = React.lazy(() => import('@/pages/Error/UnauthorizedPage'));
const NotFoundPage = React.lazy(() => import('@/pages/Error/NotFoundPage'));

// Development Pages
const DesignSystemDemo = React.lazy(() => import('@/pages/DesignSystemDemo'));
const SimpleFormSyncDemo = React.lazy(() => import('@/pages/SimpleFormSyncDemo'));

const HomePage: React.FC = () => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <React.Suspense fallback={<LoadingSpinner />}>
      <LandingPage />
    </React.Suspense>
  );
};

function App() {
  const dispatch = useAppDispatch();
  const { isInitialized, isLoading } = useAuthInitialization();

  useTokenExpiration({
    enabled: isInitialized,
    checkInterval: 1,
    logoutBuffer: 0,
  });

  useEffect(() => {
    return authEvents.onSessionExpired(() => {
      dispatch(clearAuth());
    });
  }, [dispatch]);

  // Show loading while initializing authentication
  if (!isInitialized && isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-medical-50">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-medical-600">Inicializando autenticación...</p>
        </div>
      </div>
    );
  }

  return (
    <LocaleProvider>
      <MedicalNavigationProvider>
        <ReportExportProvider>
        <Helmet>
          <title>GynHub Cloud</title>
          <meta
            name="description"
            content="Sistema médico moderno para ginecología y obstetricia con procesamiento DICOM y terminología LOINC"
          />
        </Helmet>

        <Routes>
          {/* Landing Page - redirect authenticated users to dashboard */}
          <Route path="/" element={<HomePage />} />

          {/* Auth routes */}
          <Route
            path="/auth/login"
            element={
              <ProtectedRoute guardType="public">
                <React.Suspense fallback={<LoadingSpinner />}>
                  <LoginPage />
                </React.Suspense>
              </ProtectedRoute>
            }
          />

          <Route
            path="/auth/register"
            element={
              <ProtectedRoute guardType="public">
                <React.Suspense fallback={<LoadingSpinner />}>
                  <RegisterPage />
                </React.Suspense>
              </ProtectedRoute>
            }
          />

          <Route
            path="/auth/forgot-password"
            element={
              <ProtectedRoute guardType="public">
                <React.Suspense fallback={<LoadingSpinner />}>
                  {React.createElement(React.lazy(() => import('@/pages/Auth/ForgotPasswordPage')))}
                </React.Suspense>
              </ProtectedRoute>
            }
          />

          <Route
            path="/auth/verify-email"
            element={
              <ProtectedRoute guardType="public">
                <React.Suspense fallback={<LoadingSpinner />}>
                  {React.createElement(React.lazy(() => import('@/pages/Auth/VerifyEmailPage')))}
                </React.Suspense>
              </ProtectedRoute>
            }
          />

          <Route
            path="/auth/verify-email/:token"
            element={
              <ProtectedRoute guardType="public">
                <React.Suspense fallback={<LoadingSpinner />}>
                  {React.createElement(React.lazy(() => import('@/pages/Auth/VerifyEmailPage')))}
                </React.Suspense>
              </ProtectedRoute>
            }
          />

          <Route
            path="/auth/reset-password/:token"
            element={
              <ProtectedRoute guardType="public">
                <React.Suspense fallback={<LoadingSpinner />}>
                  {React.createElement(React.lazy(() => import('@/pages/Auth/ResetPasswordPage')))}
                </React.Suspense>
              </ProtectedRoute>
            }
          />

          <Route
            path="/auth/change-password"
            element={
              <ProtectedRoute>
                <React.Suspense fallback={<LoadingSpinner />}>
                  {React.createElement(React.lazy(() => import('@/pages/Auth/ChangePasswordPage')))}
                </React.Suspense>
              </ProtectedRoute>
            }
          />

          {/* Legacy login route - redirect to new auth route */}
          <Route
            path="/login"
            element={
              <ProtectedRoute guardType="public">
                <React.Suspense fallback={<LoadingSpinner />}>
                  <LoginPage />
                </React.Suspense>
              </ProtectedRoute>
            }
          />

          {/* Error pages */}
          <Route
            path="/unauthorized"
            element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <UnauthorizedPage />
              </React.Suspense>
            }
          />

          {/* Design System Demo - Temporary route for development */}
          <Route
            path="/design-system"
            element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <DesignSystemDemo />
              </React.Suspense>
            }
          />

          {/* Worklist - Direct access route */}
          <Route
            path="/worklist"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={
                <React.Suspense fallback={<LoadingSpinner />}>
                  <WorklistPage />
                </React.Suspense>
              }
            />
          </Route>

          {/* Biopsias - Direct access route */}
          <Route
            path="/biopsias"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={
                <React.Suspense fallback={<LoadingSpinner />}>
                  <BiopsiasPage />
                </React.Suspense>
              }
            />
          </Route>

          {/* Form Synchronization Demo - AC 8.4.4 */}
          <Route
            path="/form-sync-demo"
            element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <SimpleFormSyncDemo />
              </React.Suspense>
            }
          />

          {/* Medical Routing Demo - User Story 6.4 */}
          <Route
            path="/routing-demo"
            element={
              <React.Suspense fallback={<LoadingSpinner />}>
                {React.createElement(React.lazy(() => import('@/pages/MedicalRoutingDemo')))}
              </React.Suspense>
            }
          />

          {/* Protected routes with Layout */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            {/* Dashboard */}
            <Route
              index
              element={
                <React.Suspense fallback={<LoadingSpinner />}>
                  <DashboardPage />
                </React.Suspense>
              }
            />

            {/* Worklist Management Route */}
            <Route
              path="worklist"
              element={
                <React.Suspense fallback={<LoadingSpinner />}>
                  <WorklistPage />
                </React.Suspense>
              }
            />

            {/* Patient Management Routes */}
            <Route
              path="patients"
              element={
                <React.Suspense fallback={<LoadingSpinner />}>
                  <PatientsPage />
                </React.Suspense>
              }
            />

            {/* Patient Detail Routes - Preserving existing backend structure */}
            <Route
              path="patient/:patientId"
              element={
                <ProtectedRoute requiresPatientContext={true}>
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <PatientDetailPage />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />

            {/* Study Routes */}
            <Route
              path="patient/:patientId/study/:studyId"
              element={
                <ProtectedRoute requiresPatientContext={true} requiresStudyContext={true}>
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <StudyViewerPage />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />

            <Route
              path="patient/:patientId/study/:studyId/measurements"
              element={
                <ProtectedRoute requiresPatientContext={true} requiresStudyContext={true}>
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <StudyMeasurementsPage />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />

            {/* DICOM Routes - Preserving existing processing workflow */}
            <Route
              path="patient/:patientId/dicom/upload"
              element={
                <ProtectedRoute requiresPatientContext={true}>
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <DicomUploadPage />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />

            <Route
              path="patient/:patientId/dicom/:studyId"
              element={
                <ProtectedRoute requiresPatientContext={true} requiresStudyContext={true}>
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <DicomViewerPage />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />

            {/* Report Routes - Mirroring existing backend patterns */}
            <Route
              path="patient/:patientId/reports"
              element={
                <ProtectedRoute requiresPatientContext={true}>
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <ReportsPage />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />

            <Route
              path="patient/:patientId/report/builder"
              element={
                <ProtectedRoute requiresPatientContext={true}>
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <ReportBuilderPage />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />

            <Route
              path="patient/:patientId/report/builder/:reportTypeId"
              element={
                <ProtectedRoute requiresPatientContext={true}>
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <ReportBuilderPage />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />

            <Route
              path="patient/:patientId/report/form/:reportTypeId"
              element={
                <ProtectedRoute requiresPatientContext={true}>
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <ReportFormPage />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />

            <Route
              path="patient/:patientId/report/dynamic/:reportTypeId"
              element={
                <ProtectedRoute requiresPatientContext={true}>
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <DynamicReportPage />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />

            {/* Combined Report Route - New multi-report functionality */}
            <Route
              path="patient/:patientId/report/combined"
              element={
                <ProtectedRoute requiresPatientContext={true}>
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <DynamicReportPage />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />

            <Route
              path="patient/:patientId/report/edit/:reportId"
              element={
                <ProtectedRoute requiresPatientContext={true}>
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <ReportEditorPage />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />

            <Route
              path="patient/:patientId/report/preview/:reportId"
              element={
                <ProtectedRoute requiresPatientContext={true}>
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <ReportPreviewPage />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />

            {/* Configuration Tester - Development only */}
            {/* <Route
            path="config-tester"
            element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <ConfigurationTester />
              </React.Suspense>
            }
          /> */}

            {/* Admin Routes */}
            <Route
              path="admin"
              element={
                <ProtectedRoute guardType="admin">
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <AdminPage />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />

            <Route
              path="admin/users"
              element={
                <ProtectedRoute guardType="admin" permissions={['manage_users']}>
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <UserManagementPage />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />

            <Route
              path="admin/institutions"
              element={
                <ProtectedRoute guardType="superuser">
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <InstitutionManagementPage />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Profile Routes - Standalone with Layout */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={
                <React.Suspense fallback={<LoadingSpinner />}>
                  {React.createElement(React.lazy(() => import('@/pages/Profile/ProfilePage')))}
                </React.Suspense>
              }
            />

            <Route
              path="signature"
              element={
                <React.Suspense fallback={<LoadingSpinner />}>
                  {React.createElement(React.lazy(() => import('@/pages/Profile/SignaturePage')))}
                </React.Suspense>
              }
            />
          </Route>

          {/* Admin Routes - Standalone with Layout */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute guardType="authenticated">
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={
                <ProtectedRoute guardType="admin">
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <AdminPage />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />

            <Route
              path="users"
              element={
                <ProtectedRoute guardType="superuser">
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <UserManagementPage />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />

            <Route
              path="institutions"
              element={
                <ProtectedRoute guardType="superuser">
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <InstitutionManagementPage />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />

            <Route
              path="settings"
              element={
                <ProtectedRoute guardType="admin">
                  <React.Suspense fallback={<LoadingSpinner />}>
                    {React.createElement(React.lazy(() => import('@/pages/Admin/SettingsPage')))}
                  </React.Suspense>
                </ProtectedRoute>
              }
            />

            <Route
              path="ai-config"
              element={
                <ProtectedRoute permissions={['manage_ai_config']}>
                  <React.Suspense fallback={<LoadingSpinner />}>
                    {React.createElement(React.lazy(() => import('@/pages/Admin/AIConfigPage')))}
                  </React.Suspense>
                </ProtectedRoute>
              }
            />

            <Route
              path="worklist-setup"
              element={
                <ProtectedRoute guardType="admin">
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <WorklistSetupPage />
                  </React.Suspense>
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Fallback routes */}
          <Route
            path="/404"
            element={
              <React.Suspense fallback={<LoadingSpinner />}>
                <NotFoundPage />
              </React.Suspense>
            }
          />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
        </ReportExportProvider>
      </MedicalNavigationProvider>
    </LocaleProvider>
  );
}

export default App;
